/**
 * Address Lookup Service
 * Frontend service for interacting with address lookup API
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:9003/api';
const ADDRESS_ENDPOINT = `${API_BASE}/addresses`;

/**
 * Get authentication headers with token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('pestcrm_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

/**
 * Handle API response and errors
 */
const handleResponse = async (response) => {
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }
  
  return data;
};

/**
 * Search addresses by postcode
 * @param {string} postcode - UK postcode to search
 * @param {Object} options - Search options
 * @param {number} options.limit - Maximum results (default: 10)
 * @returns {Promise} Address search results
 */
export const searchByPostcode = async (postcode, options = {}) => {
  const params = new URLSearchParams({
    postcode: postcode.trim(),
    ...(options.limit && { limit: options.limit })
  });
  
  const response = await fetch(`${ADDRESS_ENDPOINT}/search?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Search addresses by postcode and query
 * @param {string} postcode - UK postcode
 * @param {string} query - Building name/number search
 * @param {Object} options - Search options
 * @param {number} options.limit - Maximum results (default: 10)
 * @returns {Promise} Address search results
 */
export const searchByQuery = async (postcode, query, options = {}) => {
  const params = new URLSearchParams({
    postcode: postcode.trim(),
    q: query.trim(),
    ...(options.limit && { limit: options.limit })
  });
  
  const response = await fetch(`${ADDRESS_ENDPOINT}/search?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Lookup address by UPRN
 * @param {string} uprn - Unique Property Reference Number
 * @returns {Promise} Address lookup result
 */
export const lookupByUPRN = async (uprn) => {
  const params = new URLSearchParams({
    uprn: uprn.toString()
  });
  
  const response = await fetch(`${ADDRESS_ENDPOINT}/search?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Validate postcode format
 * @param {string} postcode - Postcode to validate
 * @returns {Promise} Validation result
 */
export const validatePostcode = async (postcode) => {
  const params = new URLSearchParams({
    postcode: postcode.trim()
  });
  
  const response = await fetch(`${ADDRESS_ENDPOINT}/validate-postcode?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Check address service health
 * @returns {Promise} Service health status
 */
export const checkHealth = async () => {
  const response = await fetch(`${ADDRESS_ENDPOINT}/health`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Clear address lookup cache (admin only)
 * @returns {Promise} Cache clear result
 */
export const clearCache = async () => {
  const response = await fetch(`${ADDRESS_ENDPOINT}/clear-cache`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Format address for display
 * @param {Object} address - Address object from API
 * @returns {string} Formatted address string
 */
export const formatAddressForDisplay = (address) => {
  if (!address) return '';
  
  const parts = [];
  
  if (address.line1) parts.push(address.line1);
  if (address.line2) parts.push(address.line2);
  if (address.line3) parts.push(address.line3);
  if (address.city) parts.push(address.city);
  if (address.county) parts.push(address.county);
  if (address.postcode) parts.push(address.postcode);
  
  return parts.join(', ');
};

/**
 * Convert API address format to form fields
 * @param {Object} address - Address object from API
 * @returns {Object} Form field values
 */
export const convertToFormFields = (address) => {
  if (!address) return {};
  
  return {
    address_line1: address.line1 || '',
    address_line2: address.line2 || '',
    address_city: address.city || address.locality || '',
    address_county: address.county || '',
    address_postcode: address.postcode || '',
    address_country: address.country || 'United Kingdom',
    address_uprn: address.uprn || '',
    // Legacy address field (backward compatibility)
    address: formatAddressForDisplay(address)
  };
};

const addressService = {
  searchByPostcode,
  searchByQuery,
  lookupByUPRN,
  validatePostcode,
  checkHealth,
  clearCache,
  formatAddressForDisplay,
  convertToFormFields
};

export default addressService;